import { useCallback, useEffect, useState } from 'react';
import { contactChecks, markChecked, unmarkChecked } from '../account/sharing.ts';

/**
 * Checking a fingerprint (docs/SHARING-PLAN.md section 3.2): compared once with the person,
 * in person or by message, then remembered, in a letter to oneself the server can't
 * change. If their key changes after, it says so.
 */

export interface Checks {
  /** Fingerprints found to match, by account id; null while loading. */
  readonly known: ReadonlyMap<string, string> | null;
  mark(userId: string, fingerprint: string): Promise<void>;
  unmark(userId: string): Promise<void>;
}

export function useChecks(): Checks {
  const [known, setKnown] = useState<ReadonlyMap<string, string> | null>(null);
  const load = useCallback(() => contactChecks().then(setKnown, () => setKnown(new Map())), []);
  useEffect(() => void load(), [load]);
  return {
    known,
    mark: async (userId, fingerprint) => {
      await markChecked(userId, fingerprint);
      await load();
    },
    unmark: async (userId) => {
      await unmarkChecked(userId);
      await load();
    },
  };
}

export function FingerprintCheck({ userId, fingerprint, checks }: { userId: string; fingerprint: string; checks: Checks }) {
  const [problem, setProblem] = useState('');
  const checked = checks.known?.get(userId);
  const act = (work: Promise<void>) => {
    setProblem('');
    work.catch(() => setProblem('That couldn’t be saved. Try again.'));
  };
  return (
    <div className={checked !== undefined && checked !== fingerprint ? 'fingerprint-check changed' : 'fingerprint-check'}>
      <p className="meta">
        Their fingerprint: <code className="fingerprint">{fingerprint}</code>
      </p>
      {checks.known === null ? null : checked === fingerprint ? (
        <p className="meta checked">
          ✓ Checked: it matched the one on their account page.{' '}
          <button type="button" className="link-button" onClick={() => act(checks.unmark(userId))}>
            Undo
          </button>
        </p>
      ) : checked !== undefined ? (
        <p className="notice-error" role="alert">
          Their key has changed since you checked it. Don’t rely on it until you’ve compared fingerprints with them again.{' '}
          <button type="button" className="link-button" onClick={() => act(checks.mark(userId, fingerprint))}>
            It matches now
          </button>
        </p>
      ) : (
        <p className="meta">
          Not checked yet: compare it with the one on their account page.{' '}
          <button type="button" className="link-button" onClick={() => act(checks.mark(userId, fingerprint))}>
            It matches
          </button>
        </p>
      )}
      {problem && <p className="notice-error">{problem}</p>}
    </div>
  );
}
